import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { bookingService } from '../services';
import hospitalService from '../services/hospitalService';

// Helper function to extract and format fare from booking
const extractAndFormatFare = (booking) => {
  console.log('💰 [extractAndFormatFare] Booking object:', booking);
  console.log('💰 [extractAndFormatFare] Booking keys:', Object.keys(booking || {}));
  
  let fare = booking.estimated_fare ||
    booking.estimatedFare ||
    booking.final_fare ||
    booking.finalFare ||
    booking.fare ||
    booking.amount ||
    booking.totalAmount ||
    booking.total_amount;

  console.log('💰 [extractAndFormatFare] Extracted fare:', fare, 'Type:', typeof fare);

  // Convert Decimal to number if needed
  if (fare && typeof fare === 'object' && fare.toString) {
    fare = parseFloat(fare.toString());
  } else if (fare && typeof fare === 'string') {
    fare = parseFloat(fare);
  }

  console.log('💰 [extractAndFormatFare] Converted fare:', fare, 'isNaN:', isNaN(fare));

  // Format as currency
  if (typeof fare === 'number' && !isNaN(fare) && fare > 0) {
    const formatted = `₹${fare.toFixed(2)}`;
    console.log('💰 [extractAndFormatFare] Final formatted fare:', formatted);
    return formatted;
  }
  console.warn('💰 [extractAndFormatFare] Could not extract valid fare, returning N/A');
  return 'N/A';
};

// Helper function to get status styles
const getStatusStyles = (type) => {
  const styles = {
    success: {
      background: '#d4edda',
      color: '#155724',
      border: '#c3e6cb'
    },
    error: {
      background: '#f8d7da',
      color: '#721c24',
      border: '#f5c6cb'
    },
    info: {
      background: '#d1ecf1',
      color: '#0c5460',
      border: '#bee5eb'
    }
  };
  return styles[type] || styles.info;
};

// Status message component
const StatusMessage = ({ status, elementId }) => {
  if (status.elementId !== elementId || !status.message) {
    return null;
  }

  const styles = getStatusStyles(status.type);

  return (
    <div style={{
      padding: '15px',
      borderRadius: '8px',
      marginTop: '15px',
      fontWeight: 500,
      background: styles.background,
      color: styles.color,
      border: `1px solid ${styles.border}`
    }}>
      {status.message}
    </div>
  );
};

// Helper function to build redirect URL
const buildRedirectUrl = (searchParams, bookingId, paymentIdValue) => {
  let redirectUrl = searchParams.get('redirectUrl');

  // Decode if it's URL encoded
  if (redirectUrl) {
    try {
      redirectUrl = decodeURIComponent(redirectUrl);
    } catch (e) {
      console.warn('Failed to decode redirectUrl, using as-is:', e);
    }
  }

  // Fallback to ambulance-instant booking page if no redirectUrl provided
  if (!redirectUrl) {
    redirectUrl = `${window.location.origin}/ambulance-instant`;
  }

  // Ensure redirectUrl is a valid URL
  try {
    const redirectUrlObj = new URL(redirectUrl);
    const redirectParams = new URLSearchParams({
      bookingId: bookingId,
      paymentStatus: 'success',
      paymentId: paymentIdValue
    });

    redirectUrlObj.search = redirectParams.toString();
    return redirectUrlObj.toString();
  } catch (urlError) {
    console.error('Invalid redirect URL:', redirectUrl, urlError);
    // Fallback: redirect to ambulance-instant with params
    return `${window.location.origin}/ambulance-instant?${new URLSearchParams({
      bookingId: bookingId,
      paymentStatus: 'success',
      paymentId: paymentIdValue
    }).toString()}`;
  }
};

// Helper function to build Razorpay options
const buildRazorpayOptions = (orderData, keyIdToUse, bookingId, onPaymentSuccess) => {
  // Amount from order response is already in paise (smallest currency unit)
  // Razorpay expects amount in paise, so use it directly
  const amountInPaise = orderData.amount;
  
  return {
    key: keyIdToUse,
    amount: amountInPaise, // Already in paise
    currency: orderData.currency,
    name: 'Ambulance Service',
    description: `Payment for Booking ${bookingId}`,
    order_id: orderData.id,
    handler: onPaymentSuccess,
    prefill: {
      name: 'Test User',
      contact: '9876543210',
      email: 'test@example.com'
    },
    method: {
      card: true,
      netbanking: true,
      wallet: true,
      upi: true
    },
    config: {
      display: {
        blocks: {
          banks: {
            name: "All payment methods",
            instruments: [
              { method: "card", issuers: ["HDFC", "ICICI", "AXIS"] },
              { method: "netbanking" },
              { method: "wallet" },
              { method: "upi" }
            ]
          }
        },
        sequence: ["block.banks"],
        preferences: {
          show_default_blocks: true
        }
      }
    },
    theme: {
      color: '#667eea'
    },
    modal: {
      ondismiss: function () {
        // This will be handled by the component
      }
    }
  };
};

// Helper function to check if payment verification is ready
const isPaymentVerificationReady = (orderId, paymentId, signature) => {
  return orderId && paymentId && signature;
};

// Helper function to check if payment initiation is ready
const isPaymentInitiationReady = (bookingId) => {
  return !!bookingId;
};

const PaymentTestPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [bookingId, setBookingId] = useState('');
  const [bookingData, setBookingData] = useState(null);
  const [estimatedFare, setEstimatedFare] = useState('');
  const [orderId, setOrderId] = useState('');
  const [paymentId, setPaymentId] = useState('');
  const [signature, setSignature] = useState('');
  const [razorpayKeyId, setRazorpayKeyId] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [status, setStatus] = useState({ message: '', type: 'info', elementId: 'payment-status' });
  const [loading, setLoading] = useState(false);

  const razorpayScriptLoaded = useRef(false);
  const razorpayScriptReady = useRef(false);

  // Ensure page can scroll
  useEffect(() => {
    // Enable scrolling on body and html
    const originalBodyOverflow = document.body.style.overflow;
    const originalBodyHeight = document.body.style.height;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalHtmlHeight = document.documentElement.style.height;
    
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    document.body.style.overflowY = 'auto';
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.height = 'auto';
    document.documentElement.style.overflowY = 'auto';
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.height = originalBodyHeight;
      document.body.style.overflowY = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.documentElement.style.height = originalHtmlHeight;
      document.documentElement.style.overflowY = originalHtmlOverflow;
    };
  }, []);

  // Load Razorpay script
  useEffect(() => {
    if (!razorpayScriptLoaded.current) {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;

      script.onload = () => {
        razorpayScriptReady.current = true;
        console.log('Razorpay script loaded successfully');
      };

      script.onerror = () => {
        console.error('Failed to load Razorpay script');
        showStatus('Failed to load Razorpay SDK. Please refresh the page.', 'error', 'payment-status');
      };

      document.body.appendChild(script);
      razorpayScriptLoaded.current = true;
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    console.log('🔐 [PaymentTestPage] ============= INITIALIZATION DIAGNOSTIC =============');
    console.log('🔐 [PaymentTestPage] Current URL:', window.location.href);
    // Safely access localStorage
    const storage = typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
    console.log('🔐 [PaymentTestPage] LocalStorage keys:', storage ? Object.keys(storage) : 'N/A (not available)');
    
    const urlBookingId = searchParams.get('bookingId');
    const autoProcess = searchParams.get('autoProcess') === 'true';
    const urlToken = searchParams.get('token');

    const tokenKeys = ['aps_jwt_token', 'jwt_token', 'token', 'auth_token', 'accessToken'];
    console.log('🔐 [PaymentTestPage] Token check:');
    tokenKeys.forEach(key => {
      const val = storage ? storage.getItem(key) : null;
      console.log(`  - ${key}: ${val ? '✅ Present (' + val.substring(0, 20) + '...)' : '❌ Missing'}`);
    });

    const existingToken = storage ? storage.getItem('aps_jwt_token') : null;
    const userData = storage ? storage.getItem('aps_user_data') : null;
    console.log('🔐 [PaymentTestPage] User data:', userData ? '✅ Present' : '❌ Missing');
    console.log('🔐 [PaymentTestPage] Hospital data:', storage && storage.getItem('aps_hospital_data') ? '✅ Present' : '❌ Missing');

    // If token is provided in URL, store it in localStorage
    if (urlToken && storage) {
      console.log('🔐 [PaymentTestPage] 📌 Token found in URL, storing it');
      storage.setItem('aps_jwt_token', urlToken);
      storage.setItem('token', urlToken);
      console.log('🔐 [PaymentTestPage] ✅ Token stored successfully');
    } else if (!existingToken) {
      console.error('🔐 [PaymentTestPage] ❌ CRITICAL: NO JWT TOKEN FOUND!');
      console.error('🔐 [PaymentTestPage] ❌ This will cause 401 errors on all API requests');
      showStatus('❌ Authentication Error: No token found. Please log in again.', 'error', 'payment-status');
    }

    console.log('🔐 [PaymentTestPage] Booking ID:', urlBookingId || '❌ Missing');
    console.log('🔐 [PaymentTestPage] ======================================================');

    if (urlBookingId) {
      setBookingId(urlBookingId);
      loadBookingDetails(urlBookingId, autoProcess);
    } else {
      showStatus('No booking ID provided. Please go back and create a booking first.', 'error', 'payment-status');
    }
  }, [searchParams]);

  const showStatus = (message, type = 'info', elementId = 'payment-status') => {
    setStatus({ message, type, elementId });
  };

  const loadBookingDetails = async (bookingId, autoProcess) => {
    setLoading(true);
    showStatus('Loading booking details...', 'info', 'payment-status');

    try {
      console.log('📚 [loadBookingDetails] Fetching booking:', bookingId);
      const result = await bookingService.getBookingDetails(bookingId);
      console.log('📚 [loadBookingDetails] API Response:', result);

      if (!result?.success || !result?.data) {
        const errorMsg = result?.message || 'Failed to load booking details';
        console.error('📚 [loadBookingDetails] Error:', errorMsg);
        showStatus(`Error: ${errorMsg}. Please check booking ID.`, 'error', 'payment-status');
        return;
      }

      const booking = result.data;
      console.log('📚 [loadBookingDetails] Booking data:', booking);
      setBookingData(booking);
      
      const fare = extractAndFormatFare(booking);
      setEstimatedFare(fare);
      console.log('📚 [loadBookingDetails] Extracted fare:', fare);
      showStatus('Booking details loaded successfully!', 'success', 'payment-status');

      // Auto-initiate payment if requested
      if (autoProcess) {
        setTimeout(() => {
          handleInitiatePayment();
        }, 1000);
      }
    } catch (error) {
      console.error('📚 [loadBookingDetails] Error fetching booking:', error);
      console.error('📚 [loadBookingDetails] Error details:', error);
      showStatus(`Error: ${error.message || 'Failed to load booking details'}. Please try again.`, 'error', 'payment-status');
    } finally {
      setLoading(false);
    }
  };

  const handleInitiatePayment = async () => {
    if (!isPaymentInitiationReady(bookingId)) {
      showStatus('Please create a booking first', 'error', 'payment-status');
      return;
    }

    if (!bookingData) {
      showStatus('Booking details not loaded. Please refresh the page.', 'error', 'payment-status');
      return;
    }

    // Extract fare from booking data
    const bookingFare = bookingData.estimated_fare ||
      bookingData.estimatedFare ||
      bookingData.final_fare ||
      bookingData.finalFare ||
      bookingData.fare ||
      bookingData.amount ||
      bookingData.totalAmount ||
      bookingData.total_amount;

    if (!bookingFare) {
      showStatus('Unable to determine booking fare. Please check booking details.', 'error', 'payment-status');
      return;
    }

    // Convert fare to string format (as required by API)
    // Handle Decimal objects or numbers
    let fareValue = bookingFare;
    if (fareValue && typeof fareValue === 'object' && fareValue.toString) {
      fareValue = parseFloat(fareValue.toString());
    } else if (fareValue && typeof fareValue === 'string') {
      fareValue = parseFloat(fareValue);
    }

    if (isNaN(fareValue) || fareValue <= 0) {
      showStatus('Invalid fare amount. Please check booking details.', 'error', 'payment-status');
      return;
    }

    // Format fare as string with 2 decimal places
    const fareString = fareValue.toFixed(2);

    setLoading(true);
    showStatus('Initiating payment...', 'info', 'payment-status');

    try {
      console.log('💳 [handleInitiatePayment] Starting payment initiation for booking:', bookingId);
      console.log('💳 [handleInitiatePayment] Booking fare:', bookingFare);
      console.log('💳 [handleInitiatePayment] Fare value:', fareValue);
      console.log('💳 [handleInitiatePayment] Fare string:', fareString);
      
      const result = await apiService.initiatePayment(bookingId, fareString);
      console.log('💳 [handleInitiatePayment] Payment initiation response:', result);

      if (!result?.success || !result?.data) {
        const errorMsg = result?.message || result?.error || 'Payment initiation failed';
        console.error('💳 [handleInitiatePayment] Payment initiation failed:', result);
        console.error('💳 [handleInitiatePayment] Response structure:', {
          success: result?.success,
          message: result?.message,
          data: result?.data,
          status: result?.status,
          error: result?.error
        });
        showStatus(`Payment initiation failed: ${errorMsg}`, 'error', 'payment-status');
        return;
      }

      const orderData = result.data.order;
      const keyId = result.data.keyId;

      if (!orderData?.id) {
        throw new Error('Invalid order data received from server');
      }

      if (!keyId) {
        throw new Error('Razorpay Key ID not received from server');
      }

      // Extract amount from order response and format as currency
      // Amount from order is in paise (smallest currency unit), convert to rupees for display
      const amountFromOrder = orderData.amount;
      if (amountFromOrder) {
        const amountInRupees = amountFromOrder / 100; // Convert paise to rupees
        const formattedFare = `₹${amountInRupees.toFixed(2)}`;
        setEstimatedFare(formattedFare);
        console.log('💳 [handleInitiatePayment] Amount from order (paise):', amountFromOrder);
        console.log('💳 [handleInitiatePayment] Amount in rupees:', amountInRupees);
        console.log('💳 [handleInitiatePayment] Formatted fare:', formattedFare);
      }

      setOrderId(orderData.id);
      setRazorpayKeyId(keyId);
      console.log('💳 [handleInitiatePayment] Order data:', orderData);
      console.log('💳 [handleInitiatePayment] Razorpay Key ID:', keyId);

      showStatus('Payment order created! Opening Razorpay checkout...', 'success', 'payment-status');
      setCurrentStep(2);
      openRazorpayCheckout(orderData, keyId);
    } catch (error) {
      console.error('💳 [handleInitiatePayment] Payment initiation error:', error);
      console.error('💳 [handleInitiatePayment] Error details:', {
        message: error.message,
        response: error.response,
        status: error.status,
        statusCode: error.response?.status,
        data: error.response?.data
      });
      showStatus(`Payment initiation failed: ${error.message || 'Unknown error'}`, 'error', 'payment-status');
    } finally {
      setLoading(false);
    }
  };

  const openRazorpayCheckout = (orderData, keyIdParam = null) => {
    const keyIdToUse = keyIdParam || razorpayKeyId;

    if (!keyIdToUse) {
      showStatus('Razorpay Key ID not available', 'error', 'payment-status');
      return;
    }

    // Wait for Razorpay script to load if not ready yet
    if (!window.Razorpay && !razorpayScriptReady.current) {
      // Wait up to 5 seconds for script to load
      let attempts = 0;
      const checkRazorpay = setInterval(() => {
        attempts++;
        if (window.Razorpay) {
          clearInterval(checkRazorpay);
          razorpayScriptReady.current = true;
          proceedWithRazorpay(orderData, keyIdToUse);
        } else if (attempts >= 50) { // 5 seconds (50 * 100ms)
          clearInterval(checkRazorpay);
          showStatus('Razorpay SDK not loaded. Please refresh the page.', 'error', 'payment-status');
        }
      }, 100);
      return;
    }

    if (!window.Razorpay) {
      showStatus('Razorpay SDK not loaded. Please refresh the page.', 'error', 'payment-status');
      return;
    }

    proceedWithRazorpay(orderData, keyIdToUse);
  };

  const proceedWithRazorpay = (orderData, keyIdToUse) => {
    if (!keyIdToUse) {
      showStatus('Razorpay Key ID not available', 'error', 'payment-status');
      return;
    }

    const onPaymentSuccess = (response) => {
      setPaymentId(response.razorpay_payment_id);
      setSignature(response.razorpay_signature);
      showStatus('Payment completed! Verifying payment...', 'success', 'payment-status');

      setTimeout(() => {
        handleVerifyPayment(response.razorpay_payment_id, response.razorpay_signature);
      }, 500);
    };

    const onPaymentDismiss = () => {
      showStatus('Payment cancelled by user. Please try again.', 'error', 'payment-status');
    };

    const options = buildRazorpayOptions(orderData, keyIdToUse, bookingId, onPaymentSuccess);
    options.modal.ondismiss = onPaymentDismiss;

    try {
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      console.log('Razorpay checkout opened successfully');
    } catch (error) {
      console.error('Error opening Razorpay checkout:', error);
      showStatus(`Failed to open Razorpay checkout: ${error.message}`, 'error', 'payment-status');
    }
  };

  const handleCreatePaymentOrder = async () => {
    if (!isPaymentInitiationReady(bookingId)) {
      showStatus('Please create a booking first', 'error', 'create-order-status');
      return;
    }

    if (!bookingData) {
      showStatus('Booking details not loaded. Please refresh the page.', 'error', 'create-order-status');
      return;
    }

    // Extract amount from booking data
    const bookingAmount = bookingData.estimated_fare ||
      bookingData.estimatedFare ||
      bookingData.final_fare ||
      bookingData.finalFare ||
      bookingData.fare ||
      bookingData.amount ||
      bookingData.totalAmount ||
      bookingData.total_amount;

    if (!bookingAmount) {
      showStatus('Unable to determine booking amount. Please check booking details.', 'error', 'create-order-status');
      return;
    }

    // Convert amount to number format
    let amountValue = bookingAmount;
    if (amountValue && typeof amountValue === 'object' && amountValue.toString) {
      amountValue = parseFloat(amountValue.toString());
    } else if (amountValue && typeof amountValue === 'string') {
      amountValue = parseFloat(amountValue);
    }

    if (isNaN(amountValue) || amountValue <= 0) {
      showStatus('Invalid amount. Please check booking details.', 'error', 'create-order-status');
      return;
    }

    setLoading(true);
    showStatus('Creating payment order...', 'info', 'create-order-status');

    try {
      console.log('💳 [handleCreatePaymentOrder] Starting payment order creation for booking:', bookingId);
      console.log('💳 [handleCreatePaymentOrder] Amount:', amountValue);
      
      // Prepare request body
      const requestBody = {
        amount: amountValue,
        currency: 'INR',
        bookingId: bookingId,
        notes: {
          BookingId: bookingId
        }
      };

      console.log('💳 [handleCreatePaymentOrder] Request body:', requestBody);
      
      const result = await hospitalService.createPaymentOrder(requestBody);
      console.log('💳 [handleCreatePaymentOrder] Payment order creation response:', result);

      if (!result?.success || !result?.data) {
        const errorMsg = result?.message || result?.error || 'Payment order creation failed';
        console.error('💳 [handleCreatePaymentOrder] Payment order creation failed:', result);
        showStatus(`Payment order creation failed: ${errorMsg}`, 'error', 'create-order-status');
        return;
      }

      const orderData = result.data.order;
      const keyId = result.data.keyId;

      if (!orderData?.id) {
        throw new Error('Invalid order data received from server');
      }

      if (!keyId) {
        throw new Error('Razorpay Key ID not received from server');
      }

      // Update estimated fare from order response
      // Amount from order is in paise (smallest currency unit), convert to rupees for display
      const amountFromOrder = orderData.amount;
      if (amountFromOrder) {
        const amountInRupees = amountFromOrder / 100; // Convert paise to rupees
        const formattedFare = `₹${amountInRupees.toFixed(2)}`;
        setEstimatedFare(formattedFare);
        console.log('💳 [handleCreatePaymentOrder] Amount from order (paise):', amountFromOrder);
        console.log('💳 [handleCreatePaymentOrder] Amount in rupees:', amountInRupees);
        console.log('💳 [handleCreatePaymentOrder] Formatted fare:', formattedFare);
      }

      setOrderId(orderData.id);
      setRazorpayKeyId(keyId);
      console.log('💳 [handleCreatePaymentOrder] Order data:', orderData);
      console.log('💳 [handleCreatePaymentOrder] Razorpay Key ID:', keyId);

      showStatus('Payment order created! Opening Razorpay checkout...', 'success', 'create-order-status');
      setCurrentStep(2);
      openRazorpayCheckout(orderData, keyId);
    } catch (error) {
      console.error('💳 [handleCreatePaymentOrder] Payment order creation error:', error);
      console.error('💳 [handleCreatePaymentOrder] Error details:', {
        message: error.message,
        response: error.response,
        status: error.status,
        statusCode: error.response?.status,
        data: error.response?.data
      });
      showStatus(`Payment order creation failed: ${error.message || 'Unknown error'}`, 'error', 'create-order-status');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPayment = async (paymentIdParam = null, signatureParam = null) => {
    const orderIdValue = orderId;
    const paymentIdValue = paymentIdParam || paymentId;
    const signatureValue = signatureParam || signature;

    if (!isPaymentVerificationReady(orderIdValue, paymentIdValue, signatureValue)) {
      showStatus('Payment verification failed: Missing payment details. Please try again.', 'error', 'verification-status');
      return;
    }

    setLoading(true);
    showStatus('Verifying payment with server...', 'info', 'verification-status');

    try {
      const result = await apiService.verifyPayment({
        orderId: orderIdValue,
        paymentId: paymentIdValue,
        signature: signatureValue
      });

      if (!result?.success) {
        const errorMsg = result?.error || result?.message || 'Payment verification failed';
        showStatus(`Payment verification failed: ${errorMsg}. Please contact support.`, 'error', 'verification-status');
        return;
      }

      const paymentStatus = result?.data?.paymentOrder?.status || result?.data?.status;

      if (paymentStatus !== 'PAID') {
        showStatus(`Payment verification failed: Payment status is ${paymentStatus}, not PAID.`, 'error', 'verification-status');
        return;
      }

      setCurrentStep(3);
      showStatus('Payment verified successfully! ✅ Initiating driver assignment...', 'success', 'verification-status');

      setTimeout(() => {
        const finalRedirectUrl = buildRedirectUrl(searchParams, bookingId, paymentIdValue);
        console.log('Redirecting to:', finalRedirectUrl);
        showStatus('Payment verified! Redirecting to booking confirmation...', 'success', 'verification-status');
        window.location.href = finalRedirectUrl;
      }, 1500);
    } catch (error) {
      console.error('Payment verification error:', error);
      showStatus(`Payment verification failed: ${error.message || 'Unknown error'}. Please contact support.`, 'error', 'verification-status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)',
      minHeight: '100vh',
      padding: '20px',
      width: '100%',
      position: 'relative'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', paddingBottom: '40px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', color: '#1e293b', marginBottom: '30px' }}>
          <h1 style={{ fontSize: '2.5em', marginBottom: '10px' }}>🚑 Payment Process</h1>
          <p style={{ fontSize: '1.1em', opacity: 0.8, color: '#475569' }}>Complete your booking payment</p>
        </div>

        {/* Step Indicator */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px' }}>
          {[1, 2, 3].map((step) => (
            <div key={step} style={{ flex: 1, textAlign: 'center', position: 'relative' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: step === currentStep ? '#667eea' : step < currentStep ? '#4caf50' : '#e0e0e0',
                color: step === currentStep || step < currentStep ? 'white' : '#666',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 10px',
                fontWeight: 'bold'
              }}>
                {step < currentStep ? '✓' : step}
              </div>
              <div style={{
                fontSize: '14px',
                color: step === currentStep ? '#667eea' : step < currentStep ? '#4caf50' : '#666',
                fontWeight: 600
              }}>
                {step === 1 ? 'Initiate Payment' : step === 2 ? 'Complete Payment' : 'Verify Payment'}
              </div>
            </div>
          ))}
        </div>

        {/* Step 1: Initiate Payment */}
        {currentStep <= 2 && (
          <div style={{
            background: 'white',
            borderRadius: '15px',
            padding: '30px',
            marginBottom: '20px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)'
          }}>
            <h2 style={{
              color: '#667eea',
              marginBottom: '20px',
              fontSize: '1.8em',
              borderBottom: '2px solid #667eea',
              paddingBottom: '10px'
            }}>Step 1: Initiate Payment</h2>

            <div style={{
              background: '#e3f2fd',
              borderLeft: '4px solid #2196f3',
              padding: '15px',
              marginBottom: '20px',
              borderRadius: '4px'
            }}>
              <strong style={{ color: '#1976d2' }}>Note:</strong> This will create a Razorpay order for your booking.
              After clicking "Initiate Payment", the Razorpay checkout will open automatically.
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontWeight: 600 }}>Booking ID</label>
              <input
                type="text"
                value={bookingId}
                readOnly
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontWeight: 600 }}>Estimated Fare</label>
              <input
                type="text"
                value={estimatedFare}
                readOnly
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            <button
              onClick={handleInitiatePayment}
              disabled={loading || !isPaymentInitiationReady(bookingId)}
              style={{
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                border: 'none',
                padding: '14px 30px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: loading || !isPaymentInitiationReady(bookingId) ? 'not-allowed' : 'pointer',
                width: '100%',
                marginTop: '10px',
                opacity: loading || !isPaymentInitiationReady(bookingId) ? 0.6 : 1
              }}
            >
              {loading ? 'Processing...' : 'Initiate Payment'}
            </button>

            <StatusMessage status={status} elementId="payment-status" />
          </div>
        )}

        {/* Hospital Payment Create Order Section */}
        {currentStep <= 2 && (
          <div style={{
            background: 'white',
            borderRadius: '15px',
            padding: '30px',
            marginBottom: '20px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
            border: '2px solid #10b981'
          }}>
            <h2 style={{
              color: '#10b981',
              marginBottom: '20px',
              fontSize: '1.8em',
              borderBottom: '2px solid #10b981',
              paddingBottom: '10px'
            }}>Hospital Payment: Create Order</h2>

            <div style={{
              background: '#d1fae5',
              borderLeft: '4px solid #10b981',
              padding: '15px',
              marginBottom: '20px',
              borderRadius: '4px'
            }}>
              <strong style={{ color: '#059669' }}>Note:</strong> This uses the hospital payment create order endpoint.
              After clicking "Create Payment Order", the Razorpay checkout will open automatically.
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontWeight: 600 }}>Booking ID</label>
              <input
                type="text"
                value={bookingId}
                readOnly
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontWeight: 600 }}>Amount (INR)</label>
              <input
                type="text"
                value={estimatedFare || ''}
                readOnly
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontWeight: 600 }}>Currency</label>
              <input
                type="text"
                value="INR"
                readOnly
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: '#f5f5f5'
                }}
              />
            </div>

            <button
              onClick={handleCreatePaymentOrder}
              disabled={loading || !isPaymentInitiationReady(bookingId)}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                padding: '14px 30px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: loading || !isPaymentInitiationReady(bookingId) ? 'not-allowed' : 'pointer',
                width: '100%',
                marginTop: '10px',
                opacity: loading || !isPaymentInitiationReady(bookingId) ? 0.6 : 1
              }}
            >
              {loading ? 'Creating Order...' : 'Create Payment Order'}
            </button>

            <StatusMessage status={status} elementId="create-order-status" />
          </div>
        )}

        {/* Step 2: Payment Verification */}
        {currentStep >= 2 && (
          <div style={{
            background: 'white',
            borderRadius: '15px',
            padding: '30px',
            marginBottom: '20px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)'
          }}>
            <h2 style={{
              color: '#667eea',
              marginBottom: '20px',
              fontSize: '1.8em',
              borderBottom: '2px solid #667eea',
              paddingBottom: '10px'
            }}>Step 2: Payment Verification</h2>

            <div style={{
              background: '#e3f2fd',
              borderLeft: '4px solid #2196f3',
              padding: '15px',
              marginBottom: '20px',
              borderRadius: '4px'
            }}>
              <strong style={{ color: '#1976d2' }}>Note:</strong> After successful payment, verify the payment using the details below.
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontWeight: 600 }}>Order ID</label>
              <input
                type="text"
                value={orderId}
                readOnly
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontWeight: 600 }}>Payment ID</label>
              <input
                type="text"
                value={paymentId}
                onChange={(e) => setPaymentId(e.target.value)}
                placeholder="Enter payment ID from Razorpay"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontWeight: 600 }}>Signature</label>
              <input
                type="text"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder="Enter signature from Razorpay"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            <button
              onClick={() => handleVerifyPayment()}
              disabled={loading || !isPaymentVerificationReady(orderId, paymentId, signature)}
              style={{
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                border: 'none',
                padding: '14px 30px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: loading || !isPaymentVerificationReady(orderId, paymentId, signature) ? 'not-allowed' : 'pointer',
                width: '100%',
                marginTop: '10px',
                opacity: loading || !isPaymentVerificationReady(orderId, paymentId, signature) ? 0.6 : 1
              }}
            >
              {loading ? 'Verifying...' : 'Verify Payment'}
            </button>

            <StatusMessage status={status} elementId="verification-status" />
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentTestPage;

